import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import { forkJoin } from 'rxjs';

interface SubIssue {
  name: string;
  has_sub_issues: boolean;
  sub_issues?: string[];
}

interface IssueStructure {
  month: string;
  year_suffix: string;
  issues: SubIssue[];
  colspan: number;
}

@Component({
  selector: 'app-business-report-indetail',
  templateUrl: './business-report-indetail.component.html',
  styleUrls: ['./business-report-indetail.component.scss']
})
export class BusinessReportIndetailComponent {
  businessReportData: any[] = [];
  contractRemainingMap: { [repName: string]: number } = {};
  isLoading: boolean = true;
  headerStructure: IssueStructure[] = [];
  financialYearDisplay: string = '';
  financialYearLabel: string = '';

  monthMarathi: { [key: string]: string } = {
    'April': 'एप्रिल',
    'May': 'मे',
    'June': 'जून',
    'July': 'जुलै',
    'August': 'ऑगस्ट',
    'September': 'सप्टेंबर',
    'October': 'ऑक्टोबर',
    'November': 'नोव्हेंबर',
    'December': 'डिसेंबर',
    'January': 'जानेवारी',
    'February': 'फेब्रुवारी',
    'March': 'मार्च'
  };

  constructor(
    private dialogRef: MatDialogRef<BusinessReportIndetailComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const payload = {
      start_date: this.data['startDate'],
      end_date: this.data['endDate'],
      financial_year: this.data['financial_year'],
      rep_name: this.data['repname'] || '',
      off_rep_name: this.data['offRepName'] || '',
      district: this.data['district'],
      taluka: this.data['taluka']
    };
    this.loadBusinessReport(payload);
  }

  // ─────────────────────────────────────────────
  // LOAD
  // ─────────────────────────────────────────────
  loadBusinessReport(payload: any): void {
    this.isLoading = true;

    const businessReport$ = this.service.getBusinessReportInDetail(payload);
    const contractReq = {
      rep_name: payload.rep_name || '',
      start_date: payload.start_date,
      end_date: payload.end_date,
      off_rep_name: payload.off_rep_name || '',
      advt_name: '',
      fiscal_year: this.data['financial_year']
    };
    const contractReport$ = this.service.getAllIndetailsContractDetails(contractReq);

    forkJoin({ business: businessReport$, contract: contractReport$ }).subscribe({
      next: ({ business, contract }: any) => {

        this.businessReportData = business.business_report || [];
        this.headerStructure = business.header_structure || [];
        this.financialYearLabel = business.financial_year_label || '';
        this.financialYearDisplay = business.financial_year_label || '';

        // ── Build contractRemainingMap keyed by rep_name ──
        this.contractRemainingMap = {};
        const contracts = contract.contracts || [];

        const marchEndPerRep: { [rep: string]: number } = {};
        const aajVaprlePerRep: { [rep: string]: number } = {};

        for (const c of contracts) {
          const bookings = c.bookings || [];

          // Sum booking amounts per rep_name (from inside each booking)
          for (const booking of bookings) {
            const repName = (booking.rep_name || '').trim();
            if (!repName) continue;
            marchEndPerRep[repName] = (marchEndPerRep[repName] || 0) + (parseFloat(booking.amount) || 0);
          }

          // aaj_akher_vaprle is at contract level
          const contractRepName = (
            c.rep_name ||
            (bookings.length > 0 ? bookings[0].rep_name : '')
          || '').trim();

          if (contractRepName) {
            aajVaprlePerRep[contractRepName] =
              (aajVaprlePerRep[contractRepName] || 0) + (parseFloat(c.aaj_akher_vaprle) || 0);
          }
        }

        // marchAkherBaki = marchEnd - aajVaprle per rep
        const allReps = new Set([...Object.keys(marchEndPerRep), ...Object.keys(aajVaprlePerRep)]);
        for (const rep of allReps) {
          this.contractRemainingMap[rep] =
            (marchEndPerRep[rep] || 0) - (aajVaprlePerRep[rep] || 0);
        }

        console.log('marchEndPerRep:', marchEndPerRep);
        console.log('aajVaprlePerRep:', aajVaprlePerRep);
        console.log('contractRemainingMap:', this.contractRemainingMap);

        // Apply jaahirat_karar_baki to each business report row
        for (const row of this.businessReportData) {
          const repName = (row.rep_name || '').trim();
          row.jaahirat_karar_baki = this.contractRemainingMap[repName] || 0;
        }

        console.log('Business rows with baki:', this.businessReportData.map(r => ({
          rep: r.rep_name,
          baki: r.jaahirat_karar_baki
        })));

        this.markOffRepLastRows();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading reports:', err);
        this.isLoading = false;
      }
    });
  }

  // ─────────────────────────────────────────────
  // ROW-LEVEL TOTALS
  // ─────────────────────────────────────────────

  /** Total of all issue columns for a single row */
  calculateRowTotal(row: any): number {
    let total = 0;
    if (this.headerStructure) {
      this.headerStructure.forEach(monthData => {
        monthData.issues.forEach(issue => {
          if (issue.has_sub_issues && issue.sub_issues) {
            issue.sub_issues.forEach(subIssue => {
              total += this.getIssueValue(row, monthData.month, monthData.year_suffix, issue.name, subIssue);
            });
          } else {
            total += this.getIssueValue(row, monthData.month, monthData.year_suffix, issue.name);
          }
        });
      });
    }
    return total;
  }

  /** Row total + its own karar baki (used only for individual data rows) */
  calculateCombinedTotal(row: any): number {
    return (this.calculateRowTotal(row) || 0) + (parseFloat(row.jaahirat_karar_baki) || 0);
  }

  // ─────────────────────────────────────────────
  // DEDUPLICATED KARAR BAKI HELPERS
  // Each rep_name is counted only ONCE, even if
  // they appear in multiple rows (district rows, etc.)
  // ─────────────────────────────────────────────

  /** Grand total of karar baki — each rep counted once */
  calculateGrandKararBakiTotal(): number {
    const seenReps = new Set<string>();
    return this.businessReportData.reduce((sum, row) => {
      const repName = (row.rep_name || '').trim();
      if (seenReps.has(repName)) return sum;
      seenReps.add(repName);
      return sum + (parseFloat(row.jaahirat_karar_baki) || 0);
    }, 0);
  }

  /** Off-rep karar baki — each rep under that off-rep counted once */
  calculateOffRepKararBakiTotal(offRepName: string): number {
    const seenReps = new Set<string>();
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName)
      .reduce((sum, row) => {
        const repName = (row.rep_name || '').trim();
        if (seenReps.has(repName)) return sum;
        seenReps.add(repName);
        return sum + (parseFloat(row.jaahirat_karar_baki) || 0);
      }, 0);
  }

  /** District karar baki — each rep in that district counted once */
  calculateDistrictKararBakiTotal(offRepName: string, district: string): number {
    const seenReps = new Set<string>();
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName && row.district === district)
      .reduce((sum, row) => {
        const repName = (row.rep_name || '').trim();
        if (seenReps.has(repName)) return sum;
        seenReps.add(repName);
        return sum + (parseFloat(row.jaahirat_karar_baki) || 0);
      }, 0);
  }

  // ─────────────────────────────────────────────
  // COMBINED TOTALS (row total + karar baki)
  // ─────────────────────────────────────────────

  /** Grand combined = grand row total + deduplicated grand karar baki */
  calculateCombinedGrandTotal(): number {
    return this.calculateGrandTotal() + this.calculateGrandKararBakiTotal();
  }

  /** Off-rep combined = off-rep row total + deduplicated off-rep karar baki */
  calculateOffRepCombinedTotal(offRepName: string): number {
    return this.calculateOffRepTotal(offRepName) + this.calculateOffRepKararBakiTotal(offRepName);
  }

  /** District combined = district row total + deduplicated district karar baki */
  calculateDistrictCombinedTotal(offRepName: string, district: string): number {
    return this.calculateDistrictRowTotal(offRepName, district) + this.calculateDistrictKararBakiTotal(offRepName, district);
  }

  // ─────────────────────────────────────────────
  // GRAND TOTALS
  // ─────────────────────────────────────────────

  /** Sum of all row totals (issue columns only) */
  calculateGrandTotal(): number {
    return this.businessReportData.reduce((sum, row) => {
      return sum + this.calculateRowTotal(row);
    }, 0);
  }

  /**
   * Column totals for footer.
   * For jaahirat_karar_baki: deduplicated sum.
   * For other fields (user_amount, etc.): straight sum.
   */
  calculateColumnTotal(field: string): number {
    if (field === 'jaahirat_karar_baki') {
      return this.calculateGrandKararBakiTotal();
    }
    return this.businessReportData.reduce((sum, row) => {
      return sum + (Number(row[field]) || 0);
    }, 0);
  }

  // ─────────────────────────────────────────────
  // OFF-REP TOTALS
  // ─────────────────────────────────────────────

  calculateOffRepTotal(offRepName: string): number {
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName)
      .reduce((sum, row) => sum + this.calculateRowTotal(row), 0);
  }

  /**
   * Off-rep column total.
   * For jaahirat_karar_baki: deduplicated.
   * For other fields: straight sum.
   */
  calculateOffRepColumnTotal(offRepName: string, field: string): number {
    if (field === 'jaahirat_karar_baki') {
      return this.calculateOffRepKararBakiTotal(offRepName);
    }
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName)
      .reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
  }

  calculateOffRepIssueTotal(offRepName: string, month: string, yearSuffix: string, issueName: string, subIssue?: string): number {
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName)
      .reduce((sum, row) => sum + (Number(this.getIssueValue(row, month, yearSuffix, issueName, subIssue)) || 0), 0);
  }

  // ─────────────────────────────────────────────
  // DISTRICT TOTALS
  // ─────────────────────────────────────────────

  calculateDistrictRowTotal(offRepName: string, district: string): number {
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName && row.district === district)
      .reduce((sum, row) => sum + this.calculateRowTotal(row), 0);
  }

  /**
   * District column total.
   * For jaahirat_karar_baki: deduplicated.
   * For other fields: straight sum.
   */
  calculateDistrictColumnTotal(offRepName: string, district: string, field: string): number {
    if (field === 'jaahirat_karar_baki') {
      return this.calculateDistrictKararBakiTotal(offRepName, district);
    }
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName && row.district === district)
      .reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
  }

  calculateDistrictIssueTotal(offRepName: string, district: string, month: string, yearSuffix: string, issueName: string, subIssue?: string): number {
    return this.businessReportData
      .filter(row => row.off_rep_name === offRepName && row.district === district)
      .reduce((sum, row) => sum + (Number(this.getIssueValue(row, month, yearSuffix, issueName, subIssue)) || 0), 0);
  }

  // ─────────────────────────────────────────────
  // ISSUE COLUMN TOTALS (footer)
  // ─────────────────────────────────────────────

  calculateIssueTotal(month: string, yearSuffix: string, issueName: string, subIssue?: string): number {
    return this.businessReportData.reduce((sum, row) => {
      return sum + this.getIssueValue(row, month, yearSuffix, issueName, subIssue);
    }, 0);
  }

  // ─────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────

  getIssueValue(row: any, month: string, year_suffix: string, issueName: string, subIssue?: string): number {
    let key: string;
    if (subIssue) {
      key = `${month}_${year_suffix}_${issueName}_${subIssue}`;
    } else {
      key = `${month}_${year_suffix}_${issueName}`;
    }
    return row.issues && row.issues[key] ? row.issues[key] : 0;
  }

  getTotalColspan(): number {
    let total = 0;
    if (this.headerStructure) {
      this.headerStructure.forEach(month => {
        total += month.colspan;
      });
    }
    return total;
  }

  getFinancialYearDisplay(): string {
    return this.financialYearLabel;
  }

  formatCurrency(value: number | null | undefined): string {
    if (!value || value === 0) return '';
    return value.toString();
  }

  // ─────────────────────────────────────────────
  // SORTING & ROW SPAN MARKING
  // ─────────────────────────────────────────────

  markOffRepLastRows(): void {
    const data = this.businessReportData;

    data.sort((a, b) => {
      const c = (a.categorywise || '').localeCompare(b.categorywise || '');
      if (c !== 0) return c;

      const o = (a.off_rep_name || '').localeCompare(b.off_rep_name || '');
      if (o !== 0) return o;

      const aIsSame = a.off_rep_name === a.rep_name ? 0 : 1;
      const bIsSame = b.off_rep_name === b.rep_name ? 0 : 1;
      if (aIsSame !== bIsSame) return aIsSame - bIsSame;

      const d = (a.district || '').localeCompare(b.district || '');
      if (d !== 0) return d;

      const t = (a.taluka || '').localeCompare(b.taluka || '');
      if (t !== 0) return t;

      return (a.rep_name || '').localeCompare(b.rep_name || '');
    });

    for (let i = 0; i < data.length; i++) {
      data[i].serial = i + 1;
    }

    // ── is_last_off_rep_row ──
    for (let i = 0; i < data.length; i++) {
      const currentOffRep = data[i].off_rep_name;
      const nextOffRep = i + 1 < data.length ? data[i + 1].off_rep_name : null;
      data[i].is_last_off_rep_row = currentOffRep !== nextOffRep;
    }

    // ── off_rep rowspan ──
    let i = 0;
    while (i < data.length) {
      const currentOffRep = data[i].off_rep_name;
      let count = 1;
      let distinctDistricts = 1;
      let prevDistrict = data[i].district;
      while (i + count < data.length && data[i + count].off_rep_name === currentOffRep) {
        if (data[i + count].district !== prevDistrict) {
          distinctDistricts++;
          prevDistrict = data[i + count].district;
        }
        count++;
      }
      data[i].is_first_off_rep_row = true;
      data[i].off_rep_rowspan = count + distinctDistricts;
      for (let j = i + 1; j < i + count; j++) {
        data[j].is_first_off_rep_row = false;
        data[j].off_rep_rowspan = 1;
      }
      i += count;
    }

    // ── district rowspan ──
    i = 0;
    while (i < data.length) {
      const currentDistrict = data[i].district;
      const currentOffRep = data[i].off_rep_name;
      let count = 1;
      while (
        i + count < data.length &&
        data[i + count].district === currentDistrict &&
        data[i + count].off_rep_name === currentOffRep
      ) {
        count++;
      }
      data[i].is_first_district_row = true;
      data[i].district_rowspan = count;
      for (let j = i + 1; j < i + count; j++) {
        data[j].is_first_district_row = false;
        data[j].district_rowspan = 1;
      }
      i += count;
    }

    // ── taluka rowspan ──
    i = 0;
    while (i < data.length) {
      const currentTaluka = data[i].taluka;
      const currentDistrict = data[i].district;
      const currentOffRep = data[i].off_rep_name;
      let count = 1;
      while (
        i + count < data.length &&
        data[i + count].taluka === currentTaluka &&
        data[i + count].district === currentDistrict &&
        data[i + count].off_rep_name === currentOffRep
      ) {
        count++;
      }
      data[i].is_first_taluka_row = true;
      data[i].taluka_rowspan = count;
      for (let j = i + 1; j < i + count; j++) {
        data[j].is_first_taluka_row = false;
        data[j].taluka_rowspan = 1;
      }
      i += count;
    }

    // ── is_last_district_row ──
    i = 0;
    while (i < data.length) {
      const currentDistrict = data[i].district;
      const currentOffRep = data[i].off_rep_name;
      let j = i;
      while (
        j < data.length &&
        data[j].district === currentDistrict &&
        data[j].off_rep_name === currentOffRep
      ) {
        data[j].is_last_district_row = false;
        j++;
      }
      data[j - 1].is_last_district_row = true;
      i = j;
    }
  }

  // ─────────────────────────────────────────────
  // EXPORT / PRINT / CLOSE
  // ─────────────────────────────────────────────

  exportToExcel(): void {
    const element = document.getElementById('business-report-table');
    if (element) {
      import('xlsx').then(xlsx => {
        const ws = xlsx.utils.table_to_sheet(element);
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'व्यवसाय अहवाल');
        const fileName = `व्यवसाय_अहवाल_${new Date().toISOString().split('T')[0]}.xlsx`;
        xlsx.writeFile(wb, fileName);
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }


  // ─────────────────────────────────────────────
// FINAL TOTAL = एकूण वयवसाय + रक्कम प्रविष्ट करा
// ─────────────────────────────────────────────

/** Row level: combined total + user_amount */
calculateFinalTotal(row: any): number {
  return (this.calculateCombinedTotal(row) || 0) + (parseFloat(row.user_amount) || 0);
}

/** District level: district combined total + sum of user_amount in that district */
calculateDistrictFinalTotal(offRepName: string, district: string): number {
  const districtCombined = this.calculateDistrictCombinedTotal(offRepName, district);
  const userAmountSum = this.businessReportData
    .filter(row => row.off_rep_name === offRepName && row.district === district)
    .reduce((sum, row) => sum + (parseFloat(row.user_amount) || 0), 0);
  return districtCombined + userAmountSum;
}

/** Off-rep level: off-rep combined total + sum of user_amount under that off-rep */
calculateOffRepFinalTotal(offRepName: string): number {
  const offRepCombined = this.calculateOffRepCombinedTotal(offRepName);
  const userAmountSum = this.businessReportData
    .filter(row => row.off_rep_name === offRepName)
    .reduce((sum, row) => sum + (parseFloat(row.user_amount) || 0), 0);
  return offRepCombined + userAmountSum;
}

/** Grand total: grand combined total + sum of all user_amount */
calculateGrandFinalTotal(): number {
  const grandCombined = this.calculateCombinedGrandTotal();
  const userAmountSum = this.businessReportData
    .reduce((sum, row) => sum + (parseFloat(row.user_amount) || 0), 0);
  return grandCombined + userAmountSum;
}

  printPageA3Portrait() {
    const printSection = document.getElementById('business-report-table');

    if (!printSection) {
      console.error('Print section not found!');
      return;
    }

    const printWindow = window.open(
      '',
      '',
      'width=1587,height=1123,top=0,left=0,scrollbars=yes'
    );

    if (!printWindow) {
      console.error('Failed to open print window');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>जाहिरात व्यवसाय अहवाल</title>
  <style>
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      box-sizing: border-box;
    }
    @page {
      size: 450mm 337mm;
      margin: 8mm 6mm;
    }
    html, body {
      margin: 0;
      padding: 0;
      width: 408mm;
      font-family: 'Arial', sans-serif;
      font-size: 10px;
      background: #fff;
    }
    .report-wrapper { width: 100%; overflow: visible; }
    table { border-collapse: collapse; width: 100%; table-layout: auto; }
    th, td {
      border: 1px solid #000;
      padding: 2px 3px;
      font-size: 9px;
      text-align: center;
      vertical-align: middle;
      white-space: nowrap;
      word-break: keep-all;
    }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    .title-bg { background-color: #2c3e50 !important; color: #ffffff !important; font-size: 11px !important; font-weight: bold; padding: 4px 6px; }
    .main-header-bg { background-color: #34495e !important; color: #ffffff !important; font-size: 10px !important; font-weight: bold; padding: 3px 5px; }
    .total-header { background-color: #4a6fa5 !important; color: #ffffff !important; font-size: 9px !important; font-weight: bold; }
    .month-header { background-color: #5b8dd9 !important; color: #ffffff !important; font-size: 9px !important; font-weight: bold; }
    .issue-header { background-color: #7aaef5 !important; color: #000 !important; font-size: 9px !important; font-weight: bold; }
    .vertical-text { writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; min-width: 18px; max-width: 24px; font-size: 8px !important; padding: 3px 1px; }
    .off-rep-cell { text-align: left !important; font-size: 10px !important; font-weight: bold; background-color: #f0f4ff !important; padding: 2px 4px; }
    .district-cell { font-size: 9px !important; font-weight: bold; background-color: #f8f9fa !important; }
    .taluka-cell, .rep-cell { font-size: 9px !important; text-align: left !important; padding: 2px 3px; }
    .amount-cell { font-size: 9px !important; text-align: right !important; padding: 2px 3px; }
    .total-cell { font-size: 9px !important; font-weight: bold; text-align: right !important; background-color: #f5f5f5 !important; padding: 2px 3px; }
    .grand-total { font-size: 9px !important; font-weight: bold; text-align: right !important; background-color: #e8eaf6 !important; padding: 2px 3px; }
    .district-total-row td { background-color: #cce5ff !important; font-weight: bold; font-size: 9px !important; text-align: right !important; }
    .subtotal-row td { background-color: #fff3cd !important; font-weight: bold; font-size: 9px !important; text-align: right !important; }
    .total-row td { background-color: #d5e8d4 !important; font-weight: bold; font-size: 10px !important; }
    .footer-total-label { text-align: right !important; padding-right: 6px; }
    input[type="number"], input[type="text"] { border: none !important; background: transparent !important; font-size: 9px !important; width: 100% !important; text-align: right; padding: 0; }
    tr { page-break-inside: avoid; }
    .subtotal-row, .district-total-row { page-break-before: avoid; page-break-after: auto; }
  </style>
</head>
<body>
  ${printSection.outerHTML}
  <script>
    window.onload = function () {
      setTimeout(function () {
        window.print();
        window.close();
      }, 500);
    };
  </script>
</body>
</html>
`);
    printWindow.document.close();
  }
}